import { useState } from 'react'
import type { RequestTypeVersionFullDTO, RequestTypeVersionListItemDTO } from '@kfolks/types'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { Button } from '@/shared/components/atoms/ui/button'
import { requestCenterApi } from '@/features/request-center'
import { computeCoarseDiff, NO_PREVIOUS_VERSION, type WorkflowConfigDiff } from './coarse-diff'
import { VersionDiffModal } from './VersionDiffModal'
import { VersionRestoreDialog } from './VersionRestoreDialog'

/**
 * Shared History tab panel — mounted by both `FormBuilderPage` and
 * `WorkflowBuilderPage` under their respective History tabs (per design D8:
 * one shared component, two consumers). Lists prior versions newest-first,
 * with a coarse diff summary on each row computed FE-side.
 *
 * Why FE-side diff (and pre-fetch all full payloads): the list endpoint
 * returns metadata-only, so to render the coarse summary we need v(N) and
 * v(N-1) full payloads. Authoring history is bounded (admin-only, ~tens
 * of versions per type in practice), so pre-fetching all full versions
 * via `useQueries` is acceptable and gives the rendered list a stable
 * shape without per-row loading flicker. React Query caches the GETs so
 * remounts and the diff modal share the data.
 */

export interface VersionHistoryPanelProps {
	requestTypeId: string
	/**
	 * Override the default Diff button behavior. By default the panel opens
	 * its own `VersionDiffModal` in-place; passing `onOpenDiff` replaces
	 * that with a caller-controlled flow (used in tests; could be used by
	 * a future page that wants the diff in a side panel instead of a modal).
	 */
	onOpenDiff?: (versionId: string, previousVersionId: string | null) => void
	/** Override the default Restore behavior; by default the panel owns the dialog + mutation. */
	onRestore?: (version: RequestTypeVersionListItemDTO) => void
}

interface DiffModalState {
	versionId: string
	previousVersionId: string | null
}

export function VersionHistoryPanel({ requestTypeId, onOpenDiff, onRestore }: VersionHistoryPanelProps) {
	const [diffModal, setDiffModal] = useState<DiffModalState | null>(null)
	const [restoreDialog, setRestoreDialog] = useState<RequestTypeVersionListItemDTO | null>(null)
	const [rowError, setRowError] = useState<{ versionId: string; messages: string[] } | null>(null)
	const queryClient = useQueryClient()
	const effectiveOnOpenDiff =
		onOpenDiff ??
		((versionId: string, previousVersionId: string | null) => setDiffModal({ versionId, previousVersionId }))
	const effectiveOnRestore =
		onRestore ??
		((version: RequestTypeVersionListItemDTO) => {
			setRowError(null)
			setRestoreDialog(version)
		})

	const restoreMutation = useMutation({
		mutationFn: (input: { versionId: string; changeNote: string | null }) =>
			requestCenterApi.restoreRequestTypeVersion(requestTypeId, input.versionId, {
				changeNote: input.changeNote,
			}),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ['request-type-versions', requestTypeId] }),
				queryClient.invalidateQueries({ queryKey: ['request-types', { admin: true }] }),
			])
			setRestoreDialog(null)
			setRowError(null)
		},
		onError: (err: unknown) => {
			const versionId = restoreDialog?.id
			if (versionId && isAxiosError(err) && err.response?.status === 422) {
				const messages = extractValidationMessages(err.response.data)
				setRowError({ versionId, messages })
				setRestoreDialog(null)
			}
		},
	})

	const list = useQuery({
		queryKey: ['request-type-versions', requestTypeId],
		queryFn: () => requestCenterApi.listRequestTypeVersions(requestTypeId),
		enabled: Boolean(requestTypeId),
	})

	const fullPayloadQueries = useQueries({
		queries: (list.data ?? []).map((v) => ({
			queryKey: ['request-type-version', requestTypeId, v.id] as const,
			queryFn: () => requestCenterApi.getRequestTypeVersion(requestTypeId, v.id),
			enabled: Boolean(requestTypeId),
		})),
	})

	if (list.isLoading) {
		return (
			<div role="status" aria-busy="true" aria-label="Loading authoring history" className="space-y-2">
				<div className="h-12 animate-pulse rounded-md bg-muted" />
				<div className="h-12 animate-pulse rounded-md bg-muted" />
				<div className="h-12 animate-pulse rounded-md bg-muted" />
			</div>
		)
	}

	if (list.isError) {
		return (
			<div
				role="alert"
				className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive"
			>
				Failed to load authoring history. Please retry.
			</div>
		)
	}

	const versions = list.data ?? []
	if (versions.length === 0) {
		return (
			<div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
				No prior versions yet.
			</div>
		)
	}

	const fullById = new Map<string, RequestTypeVersionFullDTO>()
	for (const q of fullPayloadQueries) if (q.data) fullById.set(q.data.id, q.data)

	return (
		<>
			<ol aria-label="Authoring history" className="space-y-2">
				{versions.map((v, i) => {
					const previousMeta = versions[i + 1] ?? null
					const isV1 = previousMeta === null
					return (
						<VersionRow
							key={v.id}
							version={v}
							isInitial={isV1}
							currentFull={fullById.get(v.id) ?? null}
							previousFull={previousMeta ? (fullById.get(previousMeta.id) ?? null) : null}
							onOpenDiff={() => effectiveOnOpenDiff(v.id, previousMeta?.id ?? null)}
							onRestore={() => effectiveOnRestore(v)}
							inlineError={rowError?.versionId === v.id ? rowError.messages : null}
						/>
					)
				})}
			</ol>
			{!onOpenDiff && diffModal ? (
				<VersionDiffModal
					open={true}
					onClose={() => setDiffModal(null)}
					requestTypeId={requestTypeId}
					versionId={diffModal.versionId}
					previousVersionId={diffModal.previousVersionId}
				/>
			) : null}
			{!onRestore ? (
				<VersionRestoreDialog
					open={Boolean(restoreDialog)}
					onClose={() => setRestoreDialog(null)}
					version={restoreDialog}
					isSubmitting={restoreMutation.isPending}
					errorMessage={restoreMutation.isError && !rowError ? 'Restore failed. Please retry.' : null}
					onConfirm={({ changeNote }) => {
						if (!restoreDialog) return
						restoreMutation.mutate({ versionId: restoreDialog.id, changeNote })
					}}
				/>
			) : null}
		</>
	)
}

// ─── Per-row component ───────────────────────────────────────────────────────

interface VersionRowProps {
	version: RequestTypeVersionListItemDTO
	isInitial: boolean
	currentFull: RequestTypeVersionFullDTO | null
	previousFull: RequestTypeVersionFullDTO | null
	onOpenDiff?: () => void
	onRestore?: () => void
	/** Per-row 422 messages from a failed restore (task 9.6). */
	inlineError?: ReadonlyArray<string> | null
}

function VersionRow({
	version,
	isInitial,
	currentFull,
	previousFull,
	onOpenDiff,
	onRestore,
	inlineError,
}: VersionRowProps) {
	const summary = isInitial
		? null
		: currentFull
			? computeCoarseDiff(currentFull, previousFull ?? NO_PREVIOUS_VERSION)
			: null

	return (
		<li className="rounded-md border bg-card p-3 text-sm" data-testid={`version-row-${version.versionNumber}`}>
			<div className="flex items-center justify-between gap-2">
				<div className="space-y-0.5">
					<p className="font-medium">
						v{version.versionNumber}
						{isInitial ? <span className="ml-2 text-muted-foreground">— Initial version</span> : null}
					</p>
					<p className="text-xs text-muted-foreground">
						{formatTimestamp(version.createdAt)} · {version.createdByUserId}
					</p>
					{version.changeNote ? <p className="text-xs italic text-muted-foreground">"{version.changeNote}"</p> : null}
				</div>
				<div className="flex shrink-0 gap-1">
					<Button
						variant="ghost"
						size="sm"
						onClick={onOpenDiff}
						disabled={!onOpenDiff || isInitial}
						aria-label={`View diff for version ${version.versionNumber}`}
					>
						Diff
					</Button>
					<Button
						variant="outline"
						size="sm"
						onClick={onRestore}
						disabled={!onRestore}
						aria-label={`Restore version ${version.versionNumber}`}
					>
						Restore
					</Button>
				</div>
			</div>
			{!isInitial ? (
				<button
					type="button"
					onClick={onOpenDiff}
					disabled={!onOpenDiff}
					className="mt-2 w-full rounded-sm bg-muted/50 p-2 text-left text-xs hover:bg-muted disabled:cursor-default disabled:hover:bg-muted/50"
					data-testid={`version-row-${version.versionNumber}-summary`}
				>
					{summary ? (
						<CoarseSummaryLine summary={summary} />
					) : (
						<span className="text-muted-foreground">Loading diff…</span>
					)}
				</button>
			) : null}
			{inlineError && inlineError.length > 0 ? (
				<div
					role="alert"
					data-testid={`version-row-${version.versionNumber}-error`}
					className="mt-2 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive"
				>
					<p className="font-medium">Restore failed (validation error):</p>
					<ul className="ml-4 list-disc">
						{inlineError.map((m, i) => (
							<li key={i}>{m}</li>
						))}
					</ul>
				</div>
			) : null}
		</li>
	)
}

function CoarseSummaryLine({ summary }: { summary: NonNullable<ReturnType<typeof computeCoarseDiff>> }) {
	return (
		<span className="space-x-2">
			<SummaryChip label="Form" state={summary.formSchema} />
			<WorkflowChip diff={summary.workflowConfig} />
			<SummaryChip label="Summary" state={summary.summaryTemplate} />
		</span>
	)
}

function SummaryChip({ label, state }: { label: string; state: 'unchanged' | 'changed' | 'added' | 'removed' }) {
	const tone = state === 'unchanged' ? 'text-muted-foreground' : 'text-amber-600 dark:text-amber-400 font-medium'
	return (
		<span className={tone}>
			{label}: {state}
		</span>
	)
}

function WorkflowChip({ diff }: { diff: WorkflowConfigDiff }) {
	if (diff.kind === 'unchanged') return <span className="text-muted-foreground">Workflow: unchanged</span>
	const delta = diff.stepCountDelta
	const deltaText =
		delta === null || delta === 0 ? '' : ` (${delta > 0 ? '+' : ''}${delta} step${Math.abs(delta) === 1 ? '' : 's'})`
	return <span className="font-medium text-amber-600 dark:text-amber-400">Workflow: changed{deltaText}</span>
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function formatTimestamp(iso: string): string {
	try {
		return new Intl.DateTimeFormat(undefined, {
			dateStyle: 'medium',
			timeStyle: 'short',
		}).format(new Date(iso))
	} catch {
		return iso
	}
}

/**
 * Pulls a flat array of human-readable messages out of the backend's 422
 * response body. The router middleware shapes ValidationError as
 * `{ error: 'ValidationError', message: '…', issues?: [...] }` — we accept
 * either shape and fall back to the top-level `message`.
 */
function extractValidationMessages(body: unknown): string[] {
	if (!body || typeof body !== 'object') return ['Validation failed.']
	const b = body as { message?: unknown; issues?: unknown }
	const messages: string[] = []
	if (Array.isArray(b.issues)) {
		for (const issue of b.issues) {
			if (issue && typeof issue === 'object' && typeof (issue as { message?: unknown }).message === 'string') {
				messages.push((issue as { message: string }).message)
			}
		}
	}
	if (messages.length === 0 && typeof b.message === 'string') messages.push(b.message)
	return messages.length > 0 ? messages : ['Validation failed.']
}
